<script setup>
import { computed } from 'vue';
import {
    formataCpf,
    formataCnpj,
    formataDataFromDb,
    formataMoedaReais
} from '@kodatec/brasil-utils';

const props = defineProps({
    solicitacao: {
        type: Object,
        required: true
    }
})

const pessoafisica = import.meta.env.VITE_TIPO_PESSOA_FISICA;

// O beneficiário pode não ter documento cadastrado (ex.: pessoa jurídica estrangeira paga via invoice).
const documentoBeneficiario = computed(() => {
    const documento = props.solicitacao?.dados?.pessoa_documento;
    if (!documento) return null;

    return props.solicitacao?.dados?.tipo_pessoa === pessoafisica
        ? `CPF: ${formataCpf(documento, false)}`
        : `CNPJ: ${formataCnpj(documento, false)}`;
});

// A descrição pode vir como uma string normal ou como uma lista ([ ]),
// seja um array real ou uma string contendo um array. Normaliza ambos.
const descricaoFormatada = computed(() => {
    let valor = props.solicitacao?.dados?.descricao;
    if (valor == null) return '';

    if (typeof valor === 'string') {
        const texto = valor.trim();
        if (texto.startsWith('[') && texto.endsWith(']')) {
            try {
                valor = JSON.parse(texto);
            } catch {
                return valor;
            }
        } else {
            return valor;
        }
    }

    if (Array.isArray(valor)) {
        return valor
            .filter((item) => item != null && String(item).trim() !== '')
            .join('<br>');
    }

    return String(valor);
});

</script>

<template>
    <h3 class="text-900 font-bold text-sm mb-3 col-12">Dados da Solicitação</h3>

    <LabelDados titulo="Beneficiário" :conteudo="solicitacao.dados.pessoa_nome" :detalhe="documentoBeneficiario" />

    <LabelDados titulo="Valor do Serviço" :conteudo="formataMoedaReais(solicitacao.dados.valor)" />

    <LabelDados v-if="solicitacao.dados.valor_recolhido" titulo="Valor recolhido"
        :conteudo="formataMoedaReais(solicitacao.dados.valor_recolhido)" />

    <LabelDados titulo="Data do serviço" :conteudo="formataDataFromDb(solicitacao.dados.data_execucao)" />

    <LabelDados titulo="Local do serviço" :conteudo="solicitacao.dados.local_prestacao_servico" />

    <LabelDados v-if="solicitacao.dados?.cbo_descricao" titulo="CBO" :conteudo="solicitacao.dados?.cbo_descricao" />

    <LabelDados titulo="Justificativa" :conteudo="solicitacao.dados.justificativa" tamanho_div="col-12" />

    <LabelDados titulo="Descrição" tamanho_div="col-12">
        <template #conteudo>
            <div v-html="descricaoFormatada" class="text-900 text-sm font-semibold"></div>
        </template>
    </LabelDados>

    <template v-if="solicitacao.dados.tem_boleto">
        <hr class="w-full border-solid border-top-1 border-bottom-none surface-border my-3">

        <h3 class="text-900 font-bold text-sm mb-3 col-12">Informações do Boleto</h3>

        <LabelDados titulo="Valor"
            :conteudo="`${formataMoedaReais(solicitacao.dados.boleto_valor)} - Data de vencimento: ${formataDataFromDb(solicitacao.dados.boleto_data_vencimento)}`"
            detalhe="Esta solicitação será paga via boleto bancário" tamanho_div="col-12" />
    </template>

    <template v-if="solicitacao.dados.pagamento_invoice">
        <hr class="w-full border-solid border-top-1 border-bottom-none surface-border my-3">

        <h3 class="text-900 font-bold text-sm mb-3 col-12">Informações do Pagamento</h3>

        <LabelDados titulo="Forma de pagamento" conteudo="Invoice"
            detalhe="Esta solicitação será paga via invoice, conforme o documento anexado" tamanho_div="col-12" />
    </template>
</template>